// frontend/src/components/NewTagModal.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { createTag } from "../api/tags";

/** Preset swatches for the colour picker; first is the backend default. */
const SWATCHES = [
  "#6366f1", "#ef4444", "#f59e0b", "#10b981",
  "#3b82f6", "#ec4899", "#8b5cf6", "#14b8a6",
];

/**
 * NewTagModal — name field + colour picker (P14-G8).
 *
 * @param {object}   props
 * @param {Function} props.onClose   Close without creating
 * @param {Function} props.onCreated Called with the new tag on success
 */
function NewTagModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState(SWATCHES[0]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const tag = await createTag({ name: name.trim(), color_hex: color });
      toast.success(`Tag "${tag.name}" created.`);
      onCreated(tag);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to create tag.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 mb-4">New Tag</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium text-gray-700 mb-1"
              htmlFor="tag-name"
            >
              Name
            </label>
            <input
              id="tag-name"
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. promo"
              maxLength={50}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1.5">
              Colour
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full transition-transform ${
                    color === c
                      ? "ring-2 ring-offset-2 ring-gray-400 scale-110"
                      : ""
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-gray-400">Preview:</span>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: color }}
            >
              {name.trim() || "tag"}
            </span>
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

NewTagModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default NewTagModal;
